const PREVIEW_FRAME_TIME = 0.1;

const getPreviewFrameTime = (duration) => {
  if (Number.isFinite(duration) && duration > 0) {
    return Math.min(PREVIEW_FRAME_TIME, duration * 0.05);
  }
  return 0.001;
};

const captureFrame = (video) => {
  const { videoWidth, videoHeight } = video;
  if (!videoWidth || !videoHeight) return null;

  const canvas = document.createElement("canvas");
  const maxDim = 480;
  const scale = Math.min(1, maxDim / Math.max(videoWidth, videoHeight));
  canvas.width = Math.round(videoWidth * scale);
  canvas.height = Math.round(videoHeight * scale);

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.8);
};

const waitForEvent = (video, eventName, timeoutMs) =>
  new Promise((resolve) => {
    let settled = false;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      video.removeEventListener(eventName, onEvent);
      clearTimeout(timer);
      resolve(result);
    };

    const onEvent = () => finish(true);
    const timer = setTimeout(() => finish(false), timeoutMs);

    video.addEventListener(eventName, onEvent);

    if (eventName === "loadeddata" && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      finish(true);
      return;
    }

    if (eventName === "loadedmetadata" && video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      finish(true);
      return;
    }

    if (eventName === "seeked" && !video.seeking) {
      finish(true);
    }
  });

const seekVideo = async (video, time) => {
  try {
    video.currentTime = time;
  } catch {
    return false;
  }
  return waitForEvent(video, "seeked", 4000);
};

const forceDecodeFrame = async (video) => {
  video.muted = true;

  try {
    await video.play();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    video.pause();
  } catch {
    // Autoplay may be blocked; seek-only path may still work.
  }
};

const cleanupVideo = (video) => {
  video.pause();
  video.removeAttribute("src");
  video.load();
  if (video.parentNode) {
    video.parentNode.removeChild(video);
  }
};

const tryCapturePoster = async (video) => {
  await forceDecodeFrame(video);

  const times = [
    getPreviewFrameTime(video.duration),
    0,
  ];

  for (const time of times) {
    await seekVideo(video, time);
    const poster = captureFrame(video);
    if (poster) return poster;
  }

  return null;
};

/**
 * Generate a JPEG poster from a blob/object URL.
 * Uses an off-DOM video element because Safari often won't decode frames
 * on in-page preview videos until playback has started.
 */
const generateVideoPoster = async (src, file = null) => {
  if (!src && !file) return null;

  const poster = await new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    runPosterCapture(src, resolve);
  });

  if (poster || !file) return poster;

  let fallbackSrc;
  try {
    fallbackSrc = URL.createObjectURL(file);
    const fallbackPoster = await new Promise((resolve) => {
      runPosterCapture(fallbackSrc, resolve);
    });
    return fallbackPoster;
  } finally {
    if (fallbackSrc) URL.revokeObjectURL(fallbackSrc);
  }
};

const runPosterCapture = (src, resolve) => {
  if (!src) {
    resolve(null);
    return;
  }

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  // Safari needs non-zero layout dimensions to decode local video frames.
  video.style.cssText =
    "position:fixed;left:0;top:0;width:320px;height:240px;opacity:0.01;pointer-events:none;z-index:-1;";

  let settled = false;

  const finish = (poster) => {
    if (settled) return;
    settled = true;
    clearTimeout(timeoutId);
    cleanupVideo(video);
    resolve(poster);
  };

  const timeoutId = setTimeout(() => finish(null), 15000);

  video.addEventListener("error", () => finish(null), { once: true });

  const run = async () => {
    const hasMetadata = await waitForEvent(video, "loadedmetadata", 8000);
    if (!hasMetadata) {
      finish(null);
      return;
    }

    await waitForEvent(video, "loadeddata", 4000);
    const poster = await tryCapturePoster(video);
    finish(poster);
  };

  document.body.appendChild(video);
  video.src = src;
  video.load();
  run().catch(() => finish(null));
};

export default generateVideoPoster;
